/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { BundleType } from '../resources/nls/bundle';
import { DatePicker } from '#UNSAFE_DatePicker';
import { type CloseDetail as DropdownCloseDetail } from '#UNSAFE_Dropdown';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { InlineUserAssistance } from '../UNSAFE_UserAssistance';
import { InputDatePicker } from './InputDatePicker';
import { dropdownStyles, userAssistanceStyles } from './themes/InputDatePickerDropdownStyles.css';
import { PositionData } from '../UNSAFE_Floating';
import { StickyPositioningDropdown } from '#PRIVATE_FormControls';
import { SectionedContent } from '#PRIVATE_FormControls';
import { type CloseDetail as SheetCloseDetail, Sheet } from '#UNSAFE_Sheet';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { SizeOptions } from '#UNSAFE_Floating/Floating';

type StickyPositioningDropdownProps = ComponentProps<typeof StickyPositioningDropdown>;
type InputDatePickerProps = ComponentProps<typeof InputDatePicker>;
type PickedPropsFromInputDatePicker = Pick<
  InputDatePickerProps,
  | 'assistiveText'
  | 'dayFormatter'
  | 'daysOutsideMonth'
  | 'helpSourceLink'
  | 'helpSourceText'
  | 'isDisabled'
  | 'isReadonly'
  | 'label'
  | 'messages'
  | 'min'
  | 'max'
  | 'monthAndYearPicker'
  | 'todayButton'
  | 'todayTimeZone'
  | 'userAssistanceDensity'
  | 'value'
  | 'weekDisplay'
>;
type DatePickerProps = ComponentProps<typeof DatePicker>;
type PickedPropsFromDatePicker = Pick<DatePickerProps, 'value' | 'onCommit'>;
type PickedPropsFromDropdown = Omit<StickyPositioningDropdownProps, 'onClose'>;
type CloseDetail = SheetCloseDetail | DropdownCloseDetail;

type Props = PickedPropsFromDatePicker &
  PickedPropsFromInputDatePicker &
  PickedPropsFromDropdown &
  TestIdProps & {
    /**
     * Called when the dropdown or sheet is supposed to be closed.
     */
    onClose?: (detail: CloseDetail) => void;
  };

/**
 * Component that renders DatePicker either inside a dropdown or sheet; at small screen
 * widths, it renders inside a sheet. Additionally, when a dropdown is being shown beneath
 * the component, we also show any user assistance text or messages below the picker
 * because otherwise that information would be obscured from view. When a sheet is being
 * shown, it will always display UA and messages at the bottom of the sheet.
 */
const InputDatePickerDropdown = ({
  anchorRef,
  assistiveText,
  dayFormatter,
  daysOutsideMonth,
  helpSourceLink,
  helpSourceText,
  isDisabled,
  isOpen,
  isReadonly,
  label,
  onClose,
  onCommit,
  max,
  messages,
  min,
  monthAndYearPicker,
  testId,
  todayButton,
  todayTimeZone,
  userAssistanceDensity,
  value,
  weekDisplay
}: Props) => {
  // If the dropdown opens above the component, we don't want to show UA or messages.
  const [isDropdownAbove, setDropdownAbove] = useState(false);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const handleDropdownPosition = useCallback<
    NonNullable<StickyPositioningDropdownProps['onPosition']>
  >((data: PositionData) => {
    setDropdownAbove(data.placement.startsWith('top'));
  }, []);
  // Reset the flag when the dropdown is closed.
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setDropdownAbove(false);
    }
  }

  const sizeOptions: SizeOptions = {
    isMaxWidthAdjusted: false,
    isMaxHeightAdjusted: true
  };

  // On mobile phones, the dropdown becomes a sheet.
  const shouldShowSheet = isPhone();

  const { datePicker_label } = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const datePickerAriaLabel = datePicker_label();

  const datePickerMainContent = (
    <DatePicker
      dayFormatter={dayFormatter}
      daysOutsideMonth={daysOutsideMonth}
      height={!shouldShowSheet ? 'auto' : undefined}
      onCommit={onCommit}
      max={max}
      min={min}
      // for sheet, we want the width to grow to the width of the sheet, otherwise it will use the theme default.
      maxWidth={shouldShowSheet ? '100%' : undefined}
      width="100%"
      monthAndYearPicker={monthAndYearPicker}
      testId={testId}
      todayTimeZone={todayTimeZone}
      todayButton={todayButton}
      value={value}
      weekDisplay={weekDisplay}
    />
  );

  const inlineUserAssistanceFooter =
    isOpen &&
    (!isDropdownAbove || shouldShowSheet) &&
    !isDisabled &&
    !isReadonly &&
    (assistiveText || helpSourceLink || (messages && messages.length > 0)) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <FormFieldContext.Provider value={{ isFocused: true }}>
        <div class={userAssistanceStyles}>
          <InlineUserAssistance
            assistiveText={assistiveText}
            fieldLabel={label}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            messages={messages}
            userAssistanceDensity={userAssistanceDensity}
          />
        </div>
      </FormFieldContext.Provider>
    ) : undefined;

  const contentTestId = testId ? testId + (shouldShowSheet ? '_sheet' : '_dropdown') : undefined;

  // The same content is displayed either in a dropdown or sheet: date picker and inline UA.
  // Add styling to the dropdown to enforce a fixed width.
  const dropdownContent = (
    <div class={dropdownStyles}>
      <SectionedContent
        aria-label={datePickerAriaLabel}
        footer={inlineUserAssistanceFooter}
        hasFocusTrap={true}
        main={datePickerMainContent}
        role="dialog"
        testId={contentTestId}
      />
    </div>
  );

  const dropdown = shouldShowSheet ? (
    <Sheet initialFocus="firstFocusable" isOpen={isOpen} onClose={onClose}>
      <SectionedContent
        aria-label={datePickerAriaLabel}
        footer={inlineUserAssistanceFooter}
        hasFocusTrap={true}
        main={datePickerMainContent}
        role="dialog"
        testId={contentTestId}
      />
    </Sheet>
  ) : (
    <StickyPositioningDropdown
      anchorRef={anchorRef}
      initialFocus="firstFocusable"
      isOpen={isOpen}
      onClose={onClose}
      onPosition={handleDropdownPosition}
      sizeOptions={sizeOptions}>
      {dropdownContent}
    </StickyPositioningDropdown>
  );

  return dropdown;
};

const isPhone = () => {
  return getClientHints().deviceType === 'phone';
};

export { InputDatePickerDropdown, type CloseDetail };
